import { memo } from 'react';
import {
  ToolbarCheckCircleIcons,
  ToolbarClearIcons,
  ToolbarListIcons,
  ToolbarSearchIcons,
} from '../../../../../../icons/icons';
import { CandidateStatusLegend } from '../CandidateStatusLegend';
import { ExpandableSearchSectionProps } from './interfaces';
import { UnsuccessfulApplicantsActions } from './UnsuccessfulApplicantsActions';
import { Box, Chip, IconButton, InputBase, Stack, Tooltip } from '@mui/material';

export const ExpandableSearchSection = memo<ExpandableSearchSectionProps>(
  ({
    isToolbarExpanded,
    globalSearchTerm,
    setGlobalSearchTerm,
    clearSearchTerm,
    hasSearchResults,
    filteredCandidateCount,
    handleSearchPanelToggle,
    isSearchPanelShow,
    candidateCount,
    isUnsuccessfulTab,
    selectAllUnsuccessful,
    handleClearSelected,
    isClearDisabled,
    handleSendEmailToSelected,
    isSendEmailDisabled,
    selectedApplicantCount,
    candidates,
  }) => (
    <Box
      sx={{
        overflow: 'hidden',
        transition: 'all 0.5s ease-in-out',
        maxHeight: isToolbarExpanded ? 160 : 0,
        opacity: isToolbarExpanded ? 1 : 0,
      }}
      aria-hidden={!isToolbarExpanded}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ px: { xs: 1.5, sm: 2 }, pb: 1 }}
      >
        {/* Search input */}
        <Box sx={{ position: 'relative', maxWidth: 672, flex: 1 }}>
          <InputBase
            type="text"
            placeholder="Search by name or email..."
            value={globalSearchTerm}
            onChange={(e) => setGlobalSearchTerm(e.target.value)}
            inputProps={{ 'aria-label': 'Search candidates by name or email' }}
            sx={{
              width: '100%',
              height: { xs: 32, sm: 36 },
              px: 1.5,
              pl: { xs: 4.5, sm: 5 },
              pr: { xs: 5, sm: 6 },
              fontSize: '13px',
              border: '1px solid',
              borderColor: '#e2e8f0',
              borderRadius: '8px',
              bgcolor: 'rgba(255,255,255,0.8)',
              backdropFilter: 'blur(4px)',
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
              transition: 'all 0.2s',
              '&:hover': { boxShadow: '0 4px 6px rgba(0,0,0,0.05)' },
              '&.Mui-focused': {
                borderColor: '#34d399',
                boxShadow: '0 0 0 3px rgba(16,185,129,0.15), 0 4px 12px rgba(0,0,0,0.08)',
              },
              '& input::placeholder': { color: '#94a3b8' },
            }}
          />

          {/* Search icon */}
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              right: 'auto',
              pl: { xs: 1.5, sm: 2 },
              display: 'flex',
              alignItems: 'center',
              pointerEvents: 'none',
            }}
          >
            <ToolbarSearchIcons size={18} color="#10b981" />
          </Box>

          {/* Action buttons inside input */}
          {globalSearchTerm && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                left: 'auto',
                display: 'flex',
                alignItems: 'center',
                pr: 1,
              }}
            >
              {hasSearchResults && (
                <Tooltip
                  title={
                    isSearchPanelShow
                      ? 'Hide results panel'
                      : 'Show results panel'
                  }
                >
                  <IconButton
                    onClick={handleSearchPanelToggle}
                    size="small"
                    aria-label={
                      isSearchPanelShow
                        ? 'Hide results panel'
                        : 'Show results panel'
                    }
                    aria-pressed={isSearchPanelShow}
                    sx={{
                      mr: 0.5,
                      borderRadius: '8px',
                      transition: 'all 0.2s',
                      ...(isSearchPanelShow
                        ? {
                            bgcolor: '#d1fae5',
                            color: '#15803d',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                          }
                        : {
                            color: '#94a3b8',
                            '&:hover': {
                              color: '#059669',
                              bgcolor: 'rgba(236,253,245,0.8)',
                            },
                          }),
                    }}
                  >
                    <ToolbarListIcons size={16} />
                  </IconButton>
                </Tooltip>
              )}

              <Tooltip title="Clear search">
                <IconButton
                  onClick={clearSearchTerm}
                  size="small"
                  aria-label="Clear search"
                  sx={{
                    borderRadius: '8px',
                    color: '#94a3b8',
                    transition: 'all 0.2s',
                    '&:hover': {
                      color: '#ef4444',
                      bgcolor: 'rgba(254,242,242,0.8)',
                    },
                  }}
                >
                  <ToolbarClearIcons size={16} />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Box>

        {/* Search Results Info */}
        {globalSearchTerm && (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Chip
              size="small"
              icon={<ToolbarCheckCircleIcons size={12} />}
              label={
                filteredCandidateCount > 0
                  ? `${filteredCandidateCount} of ${candidateCount} found`
                  : 'No results found'
              }
              role="status"
              aria-live="polite"
              sx={{
                fontSize: '11px',
                fontWeight: 500,
                backdropFilter: 'blur(4px)',
                boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                ...(filteredCandidateCount > 0
                  ? {
                      bgcolor: 'rgba(236,253,245,0.8)',
                      color: '#15803d',
                      border: '1px solid rgba(167,243,208,0.6)',
                    }
                  : {
                      bgcolor: 'rgba(254,242,242,0.8)',
                      color: '#b91c1c',
                      border: '1px solid rgba(254,202,202,0.6)',
                    }),
              }}
            />
          </Box>
        )}

        {isUnsuccessfulTab && (
          <UnsuccessfulApplicantsActions
            selectAllUnsuccessful={selectAllUnsuccessful}
            handleClearSelected={handleClearSelected}
            isClearDisabled={isClearDisabled}
            handleSendEmailToSelected={handleSendEmailToSelected}
            isSendEmailDisabled={isSendEmailDisabled}
            selectedApplicantCount={selectedApplicantCount}
          />
        )}
      </Stack>
      <CandidateStatusLegend candidates={candidates} />
    </Box>
  ),
);

ExpandableSearchSection.displayName = 'ExpandableSearchSection';
